import type { AppliedPayeeValues } from '@/components/ReportForm/InstantPayeeSwitcher/hooks/useChangePayeeValue';
import { TOTAL_AMOUNT_TARGET_KEY } from '@/context/services/ap_mismatch_alert_settings/utils';
import { useGetApPayeeBankDetailsWithStatus } from '@/context/services/ap_payee/ApPayee.service';
import { useGetReportInferences } from '@/context/services/report_inferences/ReportInferences.service';
import {
  toAppliedDetail,
  toComparableDueDate,
  type ApReportInference,
  type AppliedInference,
  type InferenceEntry,
} from '@/context/services/report_inferences/utils';
import { useOcrContext } from '@/features/InvoiceReport/Edit/components/Context/OcrContext';
import {
  useInferenceAlertState,
  usePayeeBankInferenceAlertState,
} from '@/features/InvoiceReport/Edit/components/hooks/useInferenceAlertState';
import { useInferenceBaseline } from '@/features/InvoiceReport/Edit/components/hooks/useInferenceBaseline';
import { type InferenceAlerts } from '@/features/InvoiceReport/Edit/components/ReportFormInputs/ReportFormInputs';
import { PaymentRequestForm } from '@/features/InvoiceReport/Edit/type';
import { useDataExtractionMismatchAlertFlag } from '@/hooks/feature_flag/useDataExtractionMismatchAlertFlag';
import { useDataExtractionMismatchAlertPhase2Flag } from '@/hooks/feature_flag/useDataExtractionMismatchAlertPhase2Flag';
import { useMismatchAlert } from '@/hooks/invoice_reports/useMismatchAlert';
import { useCallback, useEffect } from 'react';
import { useWatch, type Control, type UseFormGetValues } from 'react-hook-form';

type UseReportInferenceAlertsParams = {
  reportId: string;
  reportTypeId: string;
  control: Control<PaymentRequestForm>;
  getValues: UseFormGetValues<PaymentRequestForm>;
  payeeInputId?: string;
  instantPayeeInputId?: string;
  dueDateInputId?: string;
  invoiceRegistrationNumberId?: string;
  isActiveInstantPayee?: boolean;
  instantPayeeBankName?: string;
  instantPayeeBankBranch?: string;
};

const NO_INFERENCES: ApReportInference[] = [];

export type UseReportInferenceAlertsReturn = {
  inferenceAlerts: InferenceAlerts;
  totalAmountInference: InferenceEntry | undefined;
  isTotalAmountAlertEnabled: boolean;
  applyPayeeDerivedBaseline: (applied?: AppliedPayeeValues) => void;
  appliedInferences: AppliedInference[];
};

export const useReportInferenceAlerts = ({
  reportId,
  reportTypeId,
  control,
  getValues,
  payeeInputId,
  instantPayeeInputId,
  dueDateInputId,
  invoiceRegistrationNumberId,
  isActiveInstantPayee,
  instantPayeeBankName,
  instantPayeeBankBranch,
}: UseReportInferenceAlertsParams): UseReportInferenceAlertsReturn => {
  const { reflectedInferences } = useOcrContext();

  const isMismatchAlertFlag = useDataExtractionMismatchAlertFlag();
  const isMismatchAlertPhase2Flag = useDataExtractionMismatchAlertPhase2Flag();
  const rawReportInferences = useGetReportInferences(
    reportId,
    isMismatchAlertFlag
  );
  const inferences =
    reflectedInferences ?? rawReportInferences?.inferences ?? NO_INFERENCES;

  const { baseline, appliedInferences, applyBaselineOverrides } =
    useInferenceBaseline(inferences);
  const { inferencesMap, isFieldAlertEnabled } = useMismatchAlert(
    baseline,
    reportTypeId,
    { isSpotPayee: !!isActiveInstantPayee }
  );

  /**
   * dueData that is derived from payee rule is treated as the baseline for subsequent comparisons.
   * If it is not derived (i.e. "payee only"), the previous baseline value is retained as-is.
   */
  const baselineDueDate = baseline[0]?.due_date;
  const applyPayeeDerivedBaseline = useCallback(
    (applied?: AppliedPayeeValues) => {
      if (!isMismatchAlertFlag || !isMismatchAlertPhase2Flag) return;
      const value = toComparableDueDate(applied?.dueDate);
      if (!value) return;
      applyBaselineOverrides({
        due_date: toAppliedDetail(baselineDueDate, {
          value,
          source: 'system',
          sourceDetail: 'payee',
        }),
      });
    },
    [
      isMismatchAlertFlag,
      isMismatchAlertPhase2Flag,
      applyBaselineOverrides,
      baselineDueDate,
    ]
  );

  const payeeFormValue = useWatch({ control, name: 'payee.value' });
  const instantPayeeAccountNumber = useWatch({
    control,
    name: 'payee.instantPayee.number',
  });
  const instantPayeeBankId = useWatch({
    control,
    name: 'payee.instantPayee.bankId',
  });
  const instantPayeeBankBranchId = useWatch({
    control,
    name: 'payee.instantPayee.bankBranchId',
  });
  const selectedPayeeId = payeeFormValue || undefined;

  const isRegisteredPayeeAlertSettingOn =
    !!payeeInputId && isFieldAlertEnabled(payeeInputId);
  const isInstantPayeeAlertSettingOn =
    !!instantPayeeInputId && isFieldAlertEnabled(instantPayeeInputId);

  const isPayeeAlertEnabled =
    isRegisteredPayeeAlertSettingOn && !isActiveInstantPayee;
  // Spot payee comparison follows the drawer: the InstantPayee row's own
  // setting must be on, and comparison is Phase 2 only.
  const isInstantPayeeAlertEnabled =
    isInstantPayeeAlertSettingOn &&
    !!isActiveInstantPayee &&
    isMismatchAlertPhase2Flag;
  // Empty instant-payee fields must fall back to the required check, not an
  // OCR mismatch. Strict equality would mark an empty value as mismatched,
  // surface the alert, and (because a new mismatch triggers validation) show
  // a required error on a field the user never touched. Only compare once the
  // field actually holds a value. Bank and branch names arrive after the id.
  const canCompareInstantPayeeBankName =
    Boolean(instantPayeeBankId) && instantPayeeBankName !== undefined;
  const canCompareInstantPayeeBankBranch =
    Boolean(instantPayeeBankBranchId) && instantPayeeBankBranch !== undefined;
  const hasInstantPayeeAccountNumber =
    instantPayeeAccountNumber !== undefined &&
    instantPayeeAccountNumber !== null &&
    instantPayeeAccountNumber !== '';
  // Masking drops apPayee only. Bank name / branch still need the selected
  // payee's registered details for comparison.
  const shouldLookupPayeeBankDetails =
    isPayeeAlertEnabled &&
    !!(
      inferencesMap.apPayee ||
      inferencesMap.payeeBankName ||
      inferencesMap.payeeBankBranch
    );

  const {
    accountNumber: selectedPayeeAccountNumber,
    bankName: selectedPayeeBankName,
    bankBranch: selectedPayeeBankBranch,
    isLoading: isSelectedPayeeBankDetailsLoading,
  } = useGetApPayeeBankDetailsWithStatus(
    shouldLookupPayeeBankDetails ? selectedPayeeId : undefined
  );

  const getPayeeCurrentValues = useCallback(
    () => ({
      accountNumber: selectedPayeeAccountNumber,
      bankName: selectedPayeeBankName,
      bankBranch: selectedPayeeBankBranch,
    }),
    [selectedPayeeAccountNumber, selectedPayeeBankName, selectedPayeeBankBranch]
  );
  const getInstantPayeeCurrentValues = useCallback(
    () => ({
      accountNumber:
        typeof instantPayeeAccountNumber === 'string'
          ? instantPayeeAccountNumber
          : undefined,
      bankName: instantPayeeBankName,
      bankBranch: instantPayeeBankBranch,
    }),
    [instantPayeeAccountNumber, instantPayeeBankName, instantPayeeBankBranch]
  );
  const getInstantPayeeBankName = useCallback(
    () => getInstantPayeeCurrentValues().bankName,
    [getInstantPayeeCurrentValues]
  );
  const getInstantPayeeBankBranch = useCallback(
    () => getInstantPayeeCurrentValues().bankBranch,
    [getInstantPayeeCurrentValues]
  );
  const getInstantPayeeAccountNumber = useCallback(
    () => getInstantPayeeCurrentValues().accountNumber,
    [getInstantPayeeCurrentValues]
  );
  const getDueDateComparableValue = useCallback(
    () => toComparableDueDate(getValues('payee.dueDate')),
    [getValues]
  );
  const getInvoiceRegNumComparableValue = useCallback(() => {
    if (!invoiceRegistrationNumberId) return null;
    const value = getValues('reportForm')?.[invoiceRegistrationNumberId];
    return typeof value === 'string' ? value : null;
  }, [getValues, invoiceRegistrationNumberId]);

  const payeeBankInferences = {
    bankName: inferencesMap.payeeBankName,
    bankBranch: inferencesMap.payeeBankBranch,
    accountNumber: inferencesMap.apPayee,
  };
  const payee = usePayeeBankInferenceAlertState(
    payeeBankInferences,
    getPayeeCurrentValues,
    isPayeeAlertEnabled && !isSelectedPayeeBankDetailsLoading
  );
  const instantPayeeBankNameAlert = useInferenceAlertState(
    inferencesMap.payeeBankName,
    getInstantPayeeBankName,
    isInstantPayeeAlertEnabled && canCompareInstantPayeeBankName
  );
  const instantPayeeBankBranchAlert = useInferenceAlertState(
    inferencesMap.payeeBankBranch,
    getInstantPayeeBankBranch,
    isInstantPayeeAlertEnabled && canCompareInstantPayeeBankBranch
  );
  const instantPayeeAccountNumberAlert = useInferenceAlertState(
    inferencesMap.apPayee,
    getInstantPayeeAccountNumber,
    isInstantPayeeAlertEnabled && hasInstantPayeeAccountNumber
  );

  const { onBlur: recomputePayeeAlert } = payee;
  const { onBlur: recomputeInstantPayeeBankNameAlert } =
    instantPayeeBankNameAlert;
  const { onBlur: recomputeInstantPayeeBankBranchAlert } =
    instantPayeeBankBranchAlert;
  const { onBlur: recomputeInstantPayeeAccountNumberAlert } =
    instantPayeeAccountNumberAlert;
  useEffect(() => {
    recomputePayeeAlert();
  }, [
    recomputePayeeAlert,
    selectedPayeeAccountNumber,
    selectedPayeeBankName,
    selectedPayeeBankBranch,
    isSelectedPayeeBankDetailsLoading,
  ]);
  useEffect(() => {
    recomputeInstantPayeeBankNameAlert();
  }, [recomputeInstantPayeeBankNameAlert, instantPayeeBankName]);
  useEffect(() => {
    recomputeInstantPayeeBankBranchAlert();
  }, [recomputeInstantPayeeBankBranchAlert, instantPayeeBankBranch]);
  useEffect(() => {
    recomputeInstantPayeeAccountNumberAlert();
  }, [recomputeInstantPayeeAccountNumberAlert, instantPayeeAccountNumber]);

  const dueDate = useInferenceAlertState(
    inferencesMap.dueDate,
    getDueDateComparableValue,
    !!dueDateInputId && isFieldAlertEnabled(dueDateInputId)
  );
  const invoiceRegistrationNumber = useInferenceAlertState(
    inferencesMap.invoiceRegistrationNumber,
    getInvoiceRegNumComparableValue,
    !!invoiceRegistrationNumberId &&
      isFieldAlertEnabled(invoiceRegistrationNumberId)
  );

  return {
    inferenceAlerts: {
      payee,
      instantPayeeBankName: instantPayeeBankNameAlert,
      instantPayeeBankBranch: instantPayeeBankBranchAlert,
      instantPayeeAccountNumber: instantPayeeAccountNumberAlert,
      dueDate,
      invoiceRegistrationNumber,
    },
    totalAmountInference: inferencesMap.totalAmount,
    isTotalAmountAlertEnabled: isFieldAlertEnabled(TOTAL_AMOUNT_TARGET_KEY),
    applyPayeeDerivedBaseline,
    appliedInferences,
  };
};
